import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import type { Ref } from 'vue'
import useAdFunctions from './useAdFunctions'
import { useAnalytics } from '../analytics/useAnalytics'
import type { NtpAdEvent } from '../analytics/types'
import {
  calculateFeedPosition,
  getIndexOfPlacement,
} from '../../utils/ad-ops/adFunctions'
import { watchAdVisibility } from '../../utils/ad-ops/firstAdVisible'
import {
  reportNativeAdRendered,
  reportNativeAdRequest,
} from '../../utils/ad-ops/adRequestLifecycle'
import type { AdType } from '../../utils/ad-ops/firstAdVisible'
import { resolveConsent } from '../../utils/consent/consentGate'
import debug from '../../utils/console'

const logger = debug.withTag('useNativeAd')

export type NativeAdRender = {
  element: Element | null
}

export type NativeAdClick = {
  cardIdx: string | null
  tileIdx: number | null
  tile: Element | null
}

export type NativeAdHandlers = {
  onRender: (render: NativeAdRender) => void
  onClick: (click: NativeAdClick) => void
}

export type NativeAdProps = {
  placement: string
  tilePosition: number
  isFeed: boolean
  lazyRender: boolean
  sticky: boolean
}

export type NativeAdOptions = {
  provider: Extract<AdType, 'outbrain' | 'taboola'>
  props: NativeAdProps
  wrapper: Readonly<Ref<HTMLElement | null>>
  /** Registers this instance's callbacks with its SDK. */
  subscribe: (handlers: NativeAdHandlers) => void
  /** The slot id `NtpAd_Clicked` is logged to Assertive Yield under. */
  clickSlotId: string
  eventData: (brand: string) => NtpAdEvent
  tileBrand: (tile: Element) => string
  renderBrand: (render: NativeAdRender) => string
  /** Names this instance of the placement from its index on the page. */
  nameInstance: (index: number) => void
  /** Asks the SDK to fill the widget, once consent has settled. */
  request: () => Promise<void>
}

/**
 * The lifecycle every native widget shares: render once the slot is in view
 * and consent has settled, report the render once, and report each tile
 * click.
 */
export function useNativeAd(options: NativeAdOptions) {
  const { props, wrapper, provider } = options
  const { renderWhenCurrentElementIsVisible, adClicked } = useAdFunctions()
  const { track } = useAnalytics()

  const showWidget = ref(false)
  const adBrand = ref('')
  let hasTrackedRender = false
  let stopWatchingVisibility = () => {}

  const handleRender = (render: NativeAdRender) => {
    if (hasTrackedRender) return
    hasTrackedRender = true

    adBrand.value ||= options.renderBrand(render)
    track('NtpAd_Rendered', options.eventData(adBrand.value))
    stopWatchingVisibility = watchAdVisibility(
      wrapper.value,
      provider,
      props.placement
    )
    reportNativeAdRendered(wrapper.value)
  }

  const handleClick = (click: NativeAdClick) => {
    const feedPosition = props.isFeed
      ? calculateFeedPosition(
          click.cardIdx,
          click.tileIdx === null ? null : String(click.tileIdx)
        )
      : null
    const brand = click.tile ? options.tileBrand(click.tile) : ''

    adClicked(
      {
        ...options.eventData(adBrand.value),
        ntp_ad_brand: brand || adBrand.value,
        ntp_tile_position: feedPosition ?? props.tilePosition,
      },
      options.clickSlotId
    )
  }

  async function render() {
    showWidget.value = true
    await nextTick()

    const el = wrapper.value
    if (!el || el.getAttribute('data-render-registered')) return

    options.nameInstance(getIndexOfPlacement(props.placement, el))
    el.setAttribute('data-render-registered', 'true')
    // Subscribe only once named: until then, every unrequested instance has
    // the same base placement name and would match each other's events.
    // Neither SDK can remove a callback, so an instance's callbacks outlive it
    // and must do nothing once it has left the page.
    const isLive = () => Boolean(wrapper.value?.isConnected)
    options.subscribe({
      onRender: (render) => isLive() && handleRender(render),
      onClick: (click) => isLive() && handleClick(click),
    })

    await resolveConsent()

    // The slot can unmount during the consent wait; don't request a widget
    // into a detached node.
    if (!wrapper.value?.isConnected) return

    reportNativeAdRequest(provider, props.placement, wrapper.value)
    try {
      await options.request()
    } catch (error) {
      const failure = {
        ...options.eventData(adBrand.value),
        ntp_error_message:
          error instanceof Error ? error.message : 'Unknown error',
      }
      track('NtpAd_Render_Failed', failure)
      logger.error(`[${provider}] Error rendering ad`, {
        placement: props.placement,
        error,
      })
    }
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      wrapper.value,
      props.lazyRender,
      props.sticky,
      render
    )
  })

  onBeforeUnmount(() => {
    stopWatchingVisibility()
  })

  return { showWidget, adBrand }
}
